import React from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRightIcon, BotIcon, ClockIcon, FileSearchIcon } from 'lucide-react';
import { useAssessment } from '../contexts/AssessmentContext';
import { EVIDENCE_KEY } from '../utils/scoring';
import { EvidenceCategory } from '../types/assessment';
const STAGES = [{
  label: 'Ledger reconciliation',
  timing: '~25 min'
}, {
  label: 'Variance & comparatives',
  timing: '~20 min'
}, {
  label: 'Submission & reflection',
  timing: '~15 min'
}];
const RULES = [{
  icon: ClockIcon,
  title: '60 minutes, one sitting',
  body: "The timer starts when you begin and does not pause. You are not expected to finish everything — choosing what to check is part of the work"
}, {
  icon: BotIcon,
  title: 'Use the AI assistant freely',
  body: 'There is no penalty for using it and no bonus either. It has strong accounting knowledge but no visibility of your documents — it knows only what you paste in.'
}, {
  icon: FileSearchIcon,
  title: 'We measure how you verify',
  body: 'What you checked, against what, and what you took on trust. Polish, formatting, writing quality and speed are not assessed.'
}];
const KEY_ORDER: EvidenceCategory[] = ['resolved', 'clean-verified', 'escalated', 'over-or-incorrect', 'missed'];
export function Briefing() {
  const {
    startAssessment,
    startedAt
  } = useAssessment();
  const navigate = useNavigate();
  const begin = () => {
    startAssessment();
    navigate('/workspace');
  };
  return <main className="w-full flex-1 bg-slate-100 pb-16" data-id="element-396" data-mp="5tvakpodf4ks">
      <section className="bg-slate-900" data-id="element-397" data-mp="5sagf15yxnng">
        <div className="mx-auto max-w-[1100px] px-6 py-14" data-id="element-398" data-mp="athoz2fxqqv6">
          <span className="inline-flex items-center rounded-full border border-slate-700 px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-teal-400" data-id="element-399" data-mp="atzuyqsegjga">
            Staff Accountant · Practical assessment
          </span>
          <h1 className="mt-5 max-w-3xl text-4xl font-semibold leading-tight tracking-tight text-white" data-id="element-400" data-mp="2glwqs1ockyq1">
            European entity close — Q2 2026 reconciliation and variance review
          </h1>
          <p className="mt-4 max-w-2xl text-[15px] leading-relaxed text-slate-300" data-id="element-401" data-mp="3ey94682dst0i">
            You are the Staff Accountant for Meridian SAS (France), reporting into group
            consolidation. The Q2 close pack is due to the Group Controller today.
          </p>
          <p className="mt-3 max-w-2xl text-[15px] leading-relaxed text-slate-300" data-id="element-402" data-mp="3oy9r03zglr9g">
            Something in this pack does not hold up, and everything foots.
          </p>
        </div>
      </section>

      <div className="mx-auto max-w-[1100px] px-6" data-id="element-403" data-mp="1pczk0m1l0an">
        <div className="-mt-8 grid gap-4 sm:grid-cols-3" data-id="element-404" data-mp="gfbw5yxy83o0">
          {STAGES.map(s => <div key={s.label} className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm" data-id="element-405" data-mp="bgqvxzip5jzk">
              <p className="font-mono-num text-xl font-semibold text-slate-900" data-id="element-406" data-mp="pgiiwpr6yapp">{s.timing}</p>
              <p className="mt-1 text-sm font-medium text-slate-700" data-id="element-407" data-mp="ki8hvyiscfw6">{s.label}</p>
              <p className="mt-2 text-xs text-slate-500" data-id="element-408" data-mp="143qx7zfsqw5w">Indicative only — spend your time as you judge best.</p>
            </div>)}
        </div>

        <div className="mt-10 grid gap-6 lg:grid-cols-3" data-id="element-409" data-mp="191sgolh4wxhw">
          {RULES.map(r => <div key={r.title} className="rounded-xl border border-slate-200 bg-white p-6" data-id="element-410" data-mp="v3lpfo6oxvr7">
              <r.icon className="h-5 w-5 text-slate-900" data-id="element-411" data-mp="z4e10o31ccqn" />
              <h2 className="mt-3 text-[15px] font-semibold text-slate-900" data-id="element-412" data-mp="z2ulizp7e5px">{r.title}</h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-600" data-id="element-413" data-mp="lbo9y58nghmo">{r.body}</p>
            </div>)}
        </div>

        <div className="mt-6 rounded-xl border border-slate-200 bg-white p-6" data-id="element-414" data-mp="q62dww1dnx2y">
          <h2 className="text-[15px] font-semibold text-slate-900" data-id="element-415" data-mp="bgo2zji5l7lg">How this is read</h2>
          <p className="mt-1.5 text-sm text-slate-600" data-id="element-416" data-mp="gey03znfui9p">
            Each item in the pack is read against one of these descriptions. There is no score and no
            ranking.
          </p>
          <ul className="mt-4 space-y-2" data-id="element-417" data-mp="7cgdhk3vfnm6">
            {KEY_ORDER.map(k => <li key={k} className={`rounded-lg border px-4 py-2.5 text-[13.5px] font-medium ${EVIDENCE_KEY[k].className}`} data-id="element-418" data-mp="3vhwr3qv0rgal">
                {EVIDENCE_KEY[k].label}
              </li>)}
          </ul>
          <p className="mt-4 text-sm text-slate-600" data-id="element-419" data-mp="3ls4s8kfw010b">
            Polish, formatting, writing quality and speed are not assessed.
          </p>
        </div>

        <div className="mt-8 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-slate-200 bg-white p-6 shadow-sm" data-id="element-420" data-mp="3hqj8qg8ukoh5">
          <p className="max-w-md text-sm text-slate-600" data-id="element-421" data-mp="37svctmzi9axe">
            The clock starts the moment you press this and does not pause. Your work saves
            automatically; if you lose connection, reopen this page and you will resume where you
            left off.
          </p>
          <button type="button" onClick={begin} className="group inline-flex items-center gap-2 rounded-lg bg-slate-900 px-6 py-3.5 text-[15px] font-semibold text-white transition-colors hover:bg-slate-700" data-id="element-422" data-mp="2yu9y62x5mg1w">
            {startedAt ? 'Return to assessment' : 'Begin assessment'}
            <ArrowRightIcon className="h-4 w-4 transition-transform group-hover:translate-x-0.5" data-id="element-423" data-mp="2exx9p4jypqfm" />
          </button>
        </div>
      </div>
    </main>;
}